import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, UserCheck, Lock, Phone } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import masLogo from "@/assets/mas-logo.png";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect to dashboard if user is already logged in
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary-light to-secondary py-20 md:py-32">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
        
        <div className="container relative mx-auto px-4 text-center">
          <div className="mb-6 flex justify-center">
            <img src={masLogo} alt="MAS" className="h-20 w-auto md:h-24" />
          </div>
          <h1 className="mb-4 text-5xl font-bold text-white md:text-7xl">
            Join the Matrimonial Platform
          </h1>
          <h2 className="mb-6 text-2xl font-semibold text-white/90 md:text-4xl">
            Matchmakers from our community, doing things the traditional way
          </h2>
          <p className="mx-auto mb-4 max-w-2xl text-lg text-white/90 md:text-xl">
            Between people we know, now at community-wide scale.
          </p>
          <p className="mx-auto mb-8 max-w-2xl text-base text-white/80 md:text-lg">
            Signing up goes deeper than a profile: references, your connection to the community,
            and a call with a matchmaker who gets to know you. A{" "}
            <span className="font-semibold">free community service</span> by Muslim American Society DFW.
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
            <Link to="/request-join">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto">
                Request to Join
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="w-full border-white bg-white/10 text-white hover:bg-white/20 sm:w-auto">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-foreground">How it works</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              You don't browse profiles or swipe. Matchmakers from our community get to know
              you and make introductions between people we know.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            <Card className="border-border/50 transition-shadow hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">Community Matchmakers</h3>
                <p className="text-muted-foreground">
                  Trusted matchmakers from within our own community make every introduction the traditional way, at scale
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50 transition-shadow hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/10">
                  <UserCheck className="h-6 w-6 text-secondary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">Vetted & Referenced</h3>
                <p className="text-muted-foreground">
                  Every member provides references and their connection to the community, so introductions happen between people we know
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50 transition-shadow hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">A Real Conversation</h3>
                <p className="text-muted-foreground">
                  Signing up includes a call with a matchmaker who takes the time to genuinely understand who you are and what you're looking for
                </p>
              </CardContent>
            </Card>

            <Card className="border-border/50 transition-shadow hover:shadow-lg">
              <CardContent className="p-6">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/10">
                  <Lock className="h-6 w-6 text-secondary" />
                </div>
                <h3 className="mb-2 text-xl font-semibold text-foreground">Your Privacy</h3>
                <p className="text-muted-foreground">
                  Your details stay private. Contact information is only shared once both sides accept an introduction
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-muted py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-4 text-3xl font-bold text-foreground">Ready to join?</h2>
          <p className="mx-auto mb-8 max-w-2xl text-muted-foreground">
            Request to join and a matchmaker from the platform will be in touch to get to know you.
          </p>
          <Link to="/request-join">
            <Button size="lg" className="bg-primary hover:bg-primary-dark">
              Request to Join
            </Button>
          </Link>
          <p className="mt-6 text-sm text-muted-foreground">
            Want to help make introductions?{" "}
            <Link to="/matchmaker/signup" className="underline hover:text-foreground">
              Apply to become a matchmaker
            </Link>
          </p>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
